import * as React from "react";
import Link from "next/link";
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
} from "@reach/accordion";
import { useOfferMode } from "@nju/data-access-portal-offers";
import { pushSimpleEvent } from "@nju/data-layer";
import { Checkbox } from "@nju/form";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { Button, Heading, Markdown, Separator, Text } from "@nju/ui";
import { A } from "@nju/ui";
import { IContext } from "../consents.machine";

type ISelectConsent = (input: {
  value: boolean;
  consentId: string;
  consentVersion: number;
}) => void;

type ISelectAllConsents = (checked: boolean) => void;

interface IConsentsFormProps {
  context: IContext;
  selectConsent: ISelectConsent;
  selectAllConsents: ISelectAllConsents;
  handleSubmit: () => void;
  isSaving: boolean;
  activationFee: number;
  multiOrderItemsCount?: number;
}

export function ConsentsForm({
  context,
  selectConsent,
  selectAllConsents,
  handleSubmit,
  isSaving,
  activationFee,
  multiOrderItemsCount,
}: IConsentsFormProps) {
  const mode = useOfferMode();
  const [openAccordions, setOpenAccordions] = React.useState<
    Record<string, boolean>
  >({});

  const toggleAccordion = (id: string) => {
    setOpenAccordions((prev) => {
      return {
        ...prev,
        [id]: !prev[id],
      };
    });
  };

  return (
    <>
      <Heading
        className="mb-4"
        font="ttfirs"
        tone="neutral"
        variant="h2"
        weight="bold"
      >
        zgody
      </Heading>
      <div className="grid grid-flow-col gap-3">
        <Icon height={24} name="info-circle" tone="accent" width={24} />
        <Text size="s">
          Poniższe zgody są dobrowolne. Brak zgód nie wpłynie negatywnie na
          warunki świadczenia przez nas usług. W niektórych ofertach udzielenie
          zgody wiąże się jednak z rabatem na abonament za świadczone usługi.
          Zgody są ważne dopóki jesteś naszym Klientem, chyba że je wcześniej w
          dowolnej chwili wycofasz. Nie będzie to miało wpływu na zgodność z
          prawem wykorzystania Twoich danych przed cofnięciem zgody. Jeśli
          wycofasz którąś ze zgód udzielonych tutaj, automatycznie nie uznajemy,
          że wycofujesz analogiczne zgody w ofercie Orange Flex. Jeśli chcesz
          odwołać swoje zgody w Orange Flex, wejdź na swoje konto w aplikacji.
        </Text>
      </div>
      <Separator size="m" />
      <div className="grid grid-flow-col gap-3 mb-4 justify-start">
        <Icon height={24} name="piggy-bank" tone="accent" width={28} />
        <Text size="s">
          Wyrażenie wszystkich dobrowolnych zgód zapewni Ci rabat{" "}
          {activationFee} zł na opłatę aktywacyjną
          {multiOrderItemsCount && multiOrderItemsCount > 1
            ? " na każdym numerze."
            : "."}
        </Text>
      </div>
      <div className="p-3 rounded-[12px] bg-[#EAF0F0]">
        <div className="grid grid-flow-col gap-3 mb-4 justify-start items-center">
          <Icon height={24} name="money-bill-wave" tone="accent" width={30} />
          <Text size="s">Nie chcesz płacić opłaty aktywacyjnej?</Text>
        </div>
        <div className="pl-1">
          <Checkbox
            checkboxPosition="top"
            isSelected={
              context.consentsCatalogue.length > 0 &&
              context.consentsCatalogue.every((c) => {
                return c.accepted;
              })
            }
            name="consentsSelectAll"
            onValueChange={(value) => {
              return selectAllConsents(value);
            }}
          >
            <Text className="ml-2 mt-[1px]" size="m" weight="bold">
              Zaznacz wszystkie zgody
            </Text>
          </Checkbox>
        </div>
      </div>
      {context?.consentsCatalogue?.map(
        ({ consentText, consentLongText, id, consentVersion }) => {
          return (
            <div className="mt-4" key={id}>
              <Checkbox
                checkboxPosition="top"
                isSelected={
                  !!context.consentsCatalogue.find((c) => {
                    return c.id === id;
                  })?.accepted
                }
                name={id}
                onValueChange={(value) => {
                  return selectConsent({
                    value,
                    consentId: id,
                    consentVersion,
                  });
                }}
              >
                <Text
                  className="ml-1"
                  display="block"
                  size={{ "@fromTablet": "l" }}
                  weight="bold"
                >
                  {consentText}
                </Text>
              </Checkbox>
              <Accordion
                collapsible={true}
                onChange={() => {
                  return toggleAccordion(id);
                }}
              >
                <AccordionItem>
                  <AccordionPanel className="mt-1 ml-7">
                    <Markdown className="prose-sm" content={consentLongText} />
                  </AccordionPanel>
                  <AccordionButton className="flex items-center cursor-pointer bg-transparent border-none mt-1 ml-auto">
                    <Text size="s" weight="bold">
                      {openAccordions[id] ? "zwiń" : "rozwiń"}
                    </Text>
                    <Icon
                      className="w-[13px] h-[14px] ml-2"
                      name={openAccordions[id] ? "chevron-up" : "chevron-down"}
                      tone="neutral"
                    />
                  </AccordionButton>
                </AccordionItem>
              </Accordion>
              <Separator size="xl" />
            </div>
          );
        },
      )}
      <div className="flex only-mobile:flex-col-reverse from-tablet:justify-between mt-12 only-mobile:gap-2">
        <Link href={portalRoutes.mojeKonto.index.get({ mode })} passHref={true}>
          <A
            onClick={() => {
              pushSimpleEvent({
                event: "activity_service",
                eventName: "process_finish_later",
              });
            }}
            size={{ "@onlyMobile": "s", "@fromTablet": "m" }}
            variant="secondary"
          >
            dokończ później
          </A>
        </Link>
        <Button
          isLoading={isSaving}
          onPress={handleSubmit}
          size={{ "@onlyMobile": "s", "@fromTablet": "m" }}
          testId="save-consents"
        >
          dalej
        </Button>
      </div>
    </>
  );
}
